import { makeStyles } from "components/mui-core";
import { FIGMA_COLORS } from "components-redesign/styles/colors";
import { ISOstring } from "model/modelTypes";
import React, { FC, memo, useMemo } from "react";
import { getPresetsOptions, IRangePreset } from "./presetsOptions";
import { ISODateArray } from "./utils";
import { endOfIsoDay } from "../../helpers";
import { ReactComponent as CheckIcon } from "icons/redesign/datePicker/check.svg";
import { TimeRange } from "@dataseat-dsp-client/lib-reporter";

const useStyles = makeStyles()({
  sidebar: {
    width: "182px",
    paddingTop: "65px",
    overflowY: "auto",
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: 500,
    color: FIGMA_COLORS.onSurfaceLight,
    marginBottom: "8px",
    padding: "0 16px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  quickOption: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    height: "40px",
    cursor: "pointer",
    fontSize: "14px",
    paddingLeft: "48px !important",
    color: FIGMA_COLORS.onSurfaceLight,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  quickOptionSelected: {
    backgroundColor: `rgba(29, 27, 32, 0.08) !important`,
  },
  quickOptionLabel: {
    flex: 1,
  },
  checkIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
  },
});

interface Props {
  onOptionSelect: (option: IRangePreset) => void;
  startDate: ISOstring | null;
  endDate: ISOstring | null;
  visiblePresets: TimeRange[];
}
const PresetsSidebar: FC<Props> = ({
  onOptionSelect,
  startDate,
  endDate,
  visiblePresets,
}) => {
  const { classes: styles, cx } = useStyles();
  const options = useMemo(() => getPresetsOptions(), []);

  const checkSelectedQuickLink = (value: ISODateArray) => {
    if (!startDate && !endDate) {
      return value[0] === startDate && value[1] === endDate;
    }

    if (startDate && !endDate) {
      return (
        value[0]?.toString() === startDate.toString() &&
        value[1]?.toString() === endOfIsoDay(startDate).toString()
      );
    }

    if (startDate && endDate) {
      return (
        value[0]?.toString() === startDate.toString() &&
        value[1]?.toString() === endOfIsoDay(endDate).toString()
      );
    }

    return false;
  };

  const handleOptionClick = (option: IRangePreset) => {
    onOptionSelect(option);
  };

  return (
    <div className={styles.sidebar}>
      {Object.values(options)
        .filter((ql) => visiblePresets.includes(ql.id))
        .map((option) => {
          const isSelected = checkSelectedQuickLink(option.value);
          return (
            <div
              key={option.id}
              className={cx(
                styles.quickOption,
                isSelected && styles.quickOptionSelected
              )}
              onClick={() => handleOptionClick(option)}>
              {isSelected && (
                <CheckIcon
                  fontSize="small"
                  className={styles.checkIcon}
                />
              )}
              <div className={styles.quickOptionLabel}>{option.label}</div>
            </div>
          );
        })}
    </div>
  );
};

export default memo(PresetsSidebar);
